import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, ChevronDown, Settings, User, Building2, TrendingUp, Target, Award, FileText, Phone, Zap, LogOut, Crown } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AvatarPreviewDialog } from "@/components/ui/avatar-preview-dialog";
import { Separator } from "@/components/ui/separator";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { NetworkButton } from "@/components/NetworkButton";
import { useUserChannels } from "@/hooks/useUserChannels";
import { useUserExpertise, groupExpertiseByDomain } from "@/hooks/useUserExpertise";
import { useGibRating } from "@/hooks/useGibRating";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useProfileDrawer } from "@/contexts/ProfileDrawerContext";
import { usePublicProfile } from "@/hooks/useProfile";
import { useAuth } from "@/contexts/AuthContext";
import KnowledgeBaseList from "@/components/profile/KnowledgeBaseList";
import SolutionsList from "@/components/profile/SolutionsList";
import ispaiGiblet from "@/assets/giblets/ispai-giblet.png";
import curtGiblet from "@/assets/giblets/curt-giblet.png";
import oilGasGiblet from "@/assets/giblets/oil-gas-giblet.png";
import usaGiblet from "@/assets/giblets/usa-giblet.png";
import utthungaGiblet from "@/assets/giblets/utthunga-giblet.png";
import defaultGiblet from "@/assets/giblets/default-giblet.png";

// Admin user IDs who can see all channels
const ADMIN_USER_IDS = [
  'cf475cb1-592a-4e53-a85f-ea966852b660', // Trey Fleming
  '6b8f58b8-0e2a-40b7-9986-b9905f7e2b8a'  // Enrique Barrantes
];

// Map channel slugs to their Giblet images
const channelGibletMap: Record<string, string> = {
  'ispai': ispaiGiblet,
  'curt': curtGiblet,
  'oil-gas': oilGasGiblet,
  'oil-and-gas': oilGasGiblet,
  'usa-gsa': usaGiblet,
  'usa': usaGiblet,
  'utthunga': utthungaGiblet,
};

export const ProfileDrawer = () => {
  const { isOpen, userId, closeProfileDrawer } = useProfileDrawer();
  const { user, logout } = useAuth();
  const { profile, isLoading: profileLoading } = usePublicProfile(userId || undefined);
  const isOwnProfile = user?.id === userId;
  const navigate = useNavigate();
  const {
    toast
  } = useToast();
  const {
    data: userChannels
  } = useUserChannels(profile?.id);
  const {
    data: userExpertise
  } = useUserExpertise(profile?.id);
  const {
    gibRating
  } = useGibRating(profile?.id);
  const [metrics, setMetrics] = useState({
    solutionsCount: 0,
    solutionBountiesAmount: 0,
    knowledgeBasesCount: 0,
    publishedKbsCount: 0,
    licensedKbsCount: 0,
    callsCount: 0,
    sentCallsCount: 0,
    receivedCallsCount: 0,
    // Sprint metrics restructured
    completedSprintsTotal: 0,
    createdCompletedCount: 0,
    joinedCompletedCount: 0,
    pendingSprintsCount: 0,
    inProgressSprintsCount: 0
  });
  const [detailedData, setDetailedData] = useState<{
    solutions: any[];
    knowledgeBases: any[];
    draftKbs: any[];
    mintedKbs: any[];
    licensedKbs: any[];
    calls: any[];
    createdSprints: any[];
    joinedSprints: any[];
    pendingInvitations: any[];
    inProgressSprints: any[];
  }>({
    solutions: [],
    knowledgeBases: [],
    draftKbs: [],
    mintedKbs: [],
    licensedKbs: [],
    calls: [],
    createdSprints: [],
    joinedSprints: [],
    pendingInvitations: [],
    inProgressSprints: []
  });
  useEffect(() => {
    if (profile?.id && isOpen) {
      fetchMetrics();
    }
  }, [profile?.id, isOpen]);
  const fetchMetrics = async () => {
    try {
      // Fetch solutions
      const {
        data: solutions
      } = await supabase.from('solutions').select('*').eq('creator_id', profile.id);
      const totalBounties = solutions?.reduce((sum, s) => sum + (s.bounty_amount || 0), 0) || 0;

      // Fetch knowledge bases
      const {
        data: kbs
      } = await supabase.from('knowledge_bases').select('*').eq('creator_id', profile.id).is('deleted_at', null);
      
      // Fetch minting fees to determine which KBs are actually minted/published
      const {
        data: mintingFees
      } = await supabase.from('kb_minting_fees').select('knowledge_base_id').neq('status', 'shadow');
      
      const mintedKbIds = new Set(mintingFees?.map(fee => fee.knowledge_base_id) || []);
      const mintedKbsDetail = kbs?.filter(kb => mintedKbIds.has(kb.id)) || [];
      const draftKbsDetail = kbs?.filter(kb => !mintedKbIds.has(kb.id)) || [];

      // Fetch licensed KBs (accessible but not created by user)
      // 1. Get user's channel memberships
      const { data: userChannels } = await supabase
        .from('user_organizations')
        .select('channel_id, channels(name)')
        .eq('user_id', profile.id);
      
      const channelIds = userChannels?.map(uc => uc.channel_id).filter(Boolean) || [];
      const channelNameMap = new Map(userChannels?.map(uc => [uc.channel_id, (uc.channels as any)?.name]) || []);
      
      // 2. Fetch published KBs from user's channels (not created by user)
      let channelKbs: any[] = [];
      if (channelIds.length > 0) {
        const { data: channelKbData } = await supabase
          .from('knowledge_bases')
          .select('*')
          .in('channel_id', channelIds)
          .eq('status', 'published')
          .neq('creator_id', profile.id)
          .is('deleted_at', null);
        channelKbs = (channelKbData || []).map(kb => ({
          ...kb,
          accessSource: 'channel',
          accessSourceName: channelNameMap.get(kb.channel_id) || 'Channel'
        }));
      }
      
      // 3. Fetch KBs from kb_access (not created by user)
      const { data: accessKbs } = await supabase
        .from('kb_access')
        .select('knowledge_base_id, knowledge_bases!inner(*)')
        .eq('user_id', profile.id);
      const accessKbsFiltered = (accessKbs || [])
        .filter((item: any) => item.knowledge_bases?.creator_id !== profile.id)
        .map((item: any) => ({
          ...item.knowledge_bases,
          accessSource: 'direct',
          accessSourceName: 'Direct Access'
        }));
      
      // 4. Fetch KBs from attributions (not created by user)
      const { data: attrKbs } = await supabase
        .from('attributions')
        .select('knowledge_base_id, knowledge_bases!inner(*)')
        .eq('user_id', profile.id);
      const attrKbsFiltered = (attrKbs || [])
        .filter((item: any) => item.knowledge_bases?.creator_id !== profile.id)
        .map((item: any) => ({
          ...item.knowledge_bases,
          accessSource: 'attribution',
          accessSourceName: 'Team Attribution'
        }));
      
      // Deduplicate licensed KBs by ID
      const licensedKbMap = new Map<string, any>();
      [...channelKbs, ...accessKbsFiltered, ...attrKbsFiltered].forEach(kb => {
        if (!licensedKbMap.has(kb.id)) {
          licensedKbMap.set(kb.id, kb);
        }
      });
      const licensedKbsDetail = Array.from(licensedKbMap.values());
      const licensedKbsCount = licensedKbsDetail.length;

      // Fetch calls
      const {
        data: calls
      } = await supabase.from('giblet_call_history').select('*').eq('user_id', profile.id);
      const sentCalls = calls?.filter(c => c.call_type === 'call') || [];

      // Fetch received calls (call submissions by the user)
      const {
        data: callSubmissions
      } = await supabase.from('call_submissions').select('*').eq('submitter_id', profile.id);

      // Fetch completed sprints created by user
      const {
        data: createdCompletedSprints
      } = await supabase.from('prompt_architecture_sprints').select('*').eq('creator_id', profile.id).eq('status', 'completed');

      // Fetch sprints where user is a participant but not creator (joined sprints)
      const {
        data: joinedSprintParticipants
      } = await supabase.from('sprint_participants')
        .select('*, prompt_architecture_sprints!inner(*)')
        .eq('user_id', profile.id);

      // Filter joined completed sprints (participant but not creator, and completed)
      const joinedCompletedSprints = joinedSprintParticipants?.filter(
        sp => sp.prompt_architecture_sprints?.creator_id !== profile.id && 
              sp.prompt_architecture_sprints?.status === 'completed'
      ).map(sp => sp.prompt_architecture_sprints) || [];

      // Fetch pending sprint invitations with sprint details
      const {
        data: pendingInvitations
      } = await supabase.from('sprint_invitations')
        .select('*, prompt_architecture_sprints(id, objective, status, created_at)')
        .eq('invitee_user_id', profile.id)
        .eq('status', 'pending');

      // Fetch in-progress sprints (created by user, not completed)
      const {
        data: inProgressSprints
      } = await supabase.from('prompt_architecture_sprints').select('*').eq('creator_id', profile.id).neq('status', 'completed');

      const createdCount = createdCompletedSprints?.length || 0;
      const joinedCount = joinedCompletedSprints.length;

      setMetrics({
        solutionsCount: solutions?.length || 0,
        solutionBountiesAmount: totalBounties,
        knowledgeBasesCount: kbs?.length || 0,
        publishedKbsCount: mintedKbsDetail.length,
        licensedKbsCount: licensedKbsCount,
        callsCount: calls?.length || 0,
        sentCallsCount: sentCalls.length,
        receivedCallsCount: callSubmissions?.length || 0,
        completedSprintsTotal: createdCount + joinedCount,
        createdCompletedCount: createdCount,
        joinedCompletedCount: joinedCount,
        pendingSprintsCount: pendingInvitations?.length || 0,
        inProgressSprintsCount: inProgressSprints?.length || 0
      });
      setDetailedData({
        solutions: solutions || [],
        knowledgeBases: kbs || [],
        draftKbs: draftKbsDetail,
        mintedKbs: mintedKbsDetail,
        licensedKbs: licensedKbsDetail,
        calls: calls || [],
        createdSprints: createdCompletedSprints || [],
        joinedSprints: joinedCompletedSprints,
        pendingInvitations: pendingInvitations || [],
        inProgressSprints: inProgressSprints || []
      });
    } catch (error) {
      console.error('Error fetching metrics:', error);
    }
  };
  const handleChannelClick = async (slug: string, name: string) => {
    const {
      data: {
        user
      }
    } = await supabase.auth.getUser();
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to access channels.",
        variant: "destructive"
      });
      return;
    }
          closeProfileDrawer();
    const routeMap: Record<string, string> = {
      'ispai': '/ispai-channel',
      'curt': '/curt-channel',
      'oil-and-gas': '/oil-gas-channel',
      'usa': '/usa-channel',
      'utthunga': '/utthunga-channel',
    };
    navigate(routeMap[slug] || '/ispai-channel');
  };
  // Determine if current user is an admin (can see all channels)
  const isAdmin = user?.id ? ADMIN_USER_IDS.includes(user.id) : false;
  
  // Build channels list based on user's memberships or admin status
  const channels = useMemo(() => {
    // All possible channels for admins
    const allChannels = [
      { name: "iSPAI", slug: "ispai", image: ispaiGiblet },
      { name: "CURT", slug: "curt", image: curtGiblet },
      { name: "Oil and Gas", slug: "oil-and-gas", image: oilGasGiblet },
      { name: "US Federal Government", slug: "usa", image: usaGiblet },
      { name: "Utthunga", slug: "utthunga", image: utthungaGiblet }
    ];
    
    // Admin users see all channels
    if (isAdmin) {
      return allChannels;
    }
    
    // Non-admin users see only their channels from user_organizations
    if (!userChannels || userChannels.length === 0) {
      return [];
    }
    
    return userChannels.map(uc => {
      const channelSlug = uc.channels?.slug || '';
      const channelName = uc.channels?.name || uc.organization_name;
      const gibletImage = channelGibletMap[channelSlug] || defaultGiblet;
      
      return {
        name: channelName,
        slug: channelSlug,
        image: gibletImage
      };
    });
  }, [isAdmin, userChannels]);
  
  const groupedExpertise = userExpertise ? groupExpertiseByDomain(userExpertise) : {};
  const displayRating = gibRating?.overall_rating?.toFixed(1) || "0.0";
  
  if (!userId || profileLoading) {
    return null;
  }
  
  return <Sheet open={isOpen} onOpenChange={closeProfileDrawer}>
      <SheetContent side="left" className="w-80 overflow-hidden flex flex-col">
        <SheetHeader className="mb-6 flex-shrink-0">
          <div className="flex items-center gap-3">
            <AvatarPreviewDialog
              src={profile?.avatar_url || ''}
              alt={profile?.display_name || ''}
              fallback={profile?.display_name?.split(' ').map((n: string) => n[0]).join('') || 'U'}
              className="h-12 w-12"
            />
            <div className="text-left">
              <SheetTitle className="text-lg">
                {isOwnProfile ? `Hello, ${profile?.display_name}` : profile?.display_name}
              </SheetTitle>
              {profile?.username && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <p className="text-sm text-muted-foreground cursor-help">@{profile.username}</p>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>mySPAI profile ID</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {profile?.web3_wallet_address && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <p className="text-sm text-muted-foreground cursor-help">{profile.web3_wallet_address}</p>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>digital wallet ID</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>
        </SheetHeader>

        <ScrollArea className="flex-1">
          <div className="space-y-6 pr-4">
          {/* Section 1: Profile Info */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-base flex items-center gap-2">
                <User className="h-4 w-4" />
                Profile Info
              </h3>
              <TooltipProvider>
                <div className="flex gap-2">
                  {/* BIO Button */}
                  <Popover>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <PopoverTrigger asChild>
                          <Button size="sm" className="w-14 bg-green-600 text-white hover:bg-green-700">
                            BIO
                          </Button>
                        </PopoverTrigger>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Personal bio details</p>
                      </TooltipContent>
                    </Tooltip>
                    <PopoverContent className="w-[40rem] max-w-[90vw] whitespace-pre-line">
                      {profile?.bio ? (
                        <p className="text-sm leading-6">{profile.bio}</p>
                      ) : (
                        <p className="text-sm leading-6 text-muted-foreground">
                          No bio available yet.
                        </p>
                      )}
                    </PopoverContent>
                  </Popover>

                  {/* NW Button */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <NetworkButton userId={profile?.id || ''} username={profile?.username || ''} />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{profile?.display_name?.split(' ')[0]}'s Knowledge Network</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </TooltipProvider>
            </div>
            <div className="space-y-2 text-sm">
              {profile?.title && <div className="flex items-start gap-2">
                  <Building2 className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <span>{profile.title}</span>
                </div>}
              {profile?.organization && <div className="flex items-start gap-2">
                  <Building2 className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <span>{profile.organization}</span>
                </div>}
              {userChannels && userChannels.length > 0 && <div className="flex items-start gap-2">
                  <Target className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <div className="flex flex-wrap gap-1">
                    {userChannels.map(uc => <span key={uc.channel_id} className="bg-primary/10 text-primary px-2 py-0.5 rounded text-xs">
                        {uc.channels.name}
                      </span>)}
                  </div>
                </div>}
              
              {/* Channels */}
              <div className="mt-3">
                <p className="text-xs text-muted-foreground mb-2 font-medium">Channels</p>
                <TooltipProvider>
                  <div className="flex items-center gap-4 ml-4">
                    {channels.map(channel => <Tooltip key={channel.slug}>
                        <TooltipTrigger asChild>
                          <button onClick={() => handleChannelClick(channel.slug, channel.name)} className="hover:scale-110 transition-transform">
                            <img src={channel.image} alt={channel.name} className="h-12 w-12 object-contain" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="right">
                          <p>{channel.name}</p>
                        </TooltipContent>
                      </Tooltip>)}
                  </div>
                </TooltipProvider>
              </div>

              {Object.keys(groupedExpertise).length > 0 && (
                <Collapsible className="mt-3">
                  <CollapsibleTrigger className="flex items-center gap-1 text-xs text-muted-foreground font-medium hover:text-foreground transition-colors w-full">
                    <ChevronRight className="h-3 w-3 transition-transform duration-200 [[data-state=open]>&]:rotate-90" />
                    Domains & Expertise
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-2 ml-4 mt-2">
                    {Object.entries(groupedExpertise).map(([domain, categories]) => (
                      <div key={domain} className="text-xs">
                        <p className="font-medium text-primary">{domain}</p>
                        {categories.map((cat, idx) => (
                          <p key={idx} className="ml-2 text-muted-foreground">
                            {cat.category}: {cat.subcategories.join(', ')}
                          </p>
                        ))}
                      </div>
                    ))}
                  </CollapsibleContent>
                </Collapsible>
              )}
            </div>
          </div>

          <Separator />

          {/* Section 2: Performance Metrics */}
          <div>
            <h3 className="font-bold text-base mb-3 flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Performance Metrics
            </h3>
            <div className="space-y-2 text-sm">
              <HoverCard>
                <HoverCardTrigger asChild>
                  <div 
                    className="flex items-center justify-between p-2 hover:bg-accent rounded-md transition-colors cursor-pointer"
                    onClick={() => {
                      navigate('/know-your-gib');
                      closeProfileDrawer();
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <Award className="h-4 w-4 text-muted-foreground" />
                      <span>GibRating</span>
                    </div>
                    <span className="font-semibold">{displayRating}</span>
                  </div>
                </HoverCardTrigger>
                <HoverCardContent side="right" sideOffset={16} className="w-64 bg-white dark:bg-gray-900 border shadow-lg z-50">
                  <div className="space-y-1">
                    <h4 className="text-sm font-semibold">GibRating Details</h4>
                    <p className="text-xs text-muted-foreground">Your GibRating reflects the quality and impact of your contributions to AI-Human creation, composition, and consumption of knowledge. Click to see your detailed breakdown, also known as "Know Your Gib".</p>
                    {gibRating && <div className="mt-2 space-y-1 text-xs">
                        <div className="flex justify-between"><span>Quality:</span><span>{gibRating.quality_score?.toFixed(1) || 'N/A'}</span></div>
                        <div className="flex justify-between"><span>Engagement:</span><span>{gibRating.engagement_score?.toFixed(1) || 'N/A'}</span></div>
                        <div className="flex justify-between"><span>Collaboration:</span><span>{gibRating.collaboration_score?.toFixed(1) || 'N/A'}</span></div>
                      </div>}
                  </div>
                </HoverCardContent>
              </HoverCard>

              <HoverCard>
                <HoverCardTrigger asChild>
                  <div className="flex items-center justify-between p-2 hover:bg-accent rounded-md transition-colors cursor-pointer">
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4 text-muted-foreground" />
                      <span>Solutions</span>
                    </div>
                    <span className="font-semibold">{metrics.solutionsCount}</span>
                  </div>
                </HoverCardTrigger>
                <HoverCardContent side="right" sideOffset={16} className="w-[600px] max-h-[600px] overflow-auto p-0" style={{ WebkitOverflowScrolling: 'touch' }}>
                  <SolutionsList userId={profile?.id} />
                </HoverCardContent>
              </HoverCard>

              <HoverCard>
                <HoverCardTrigger asChild>
                  <div className="flex items-center justify-between p-2 hover:bg-accent rounded-md transition-colors ml-4 cursor-pointer">
                    <span className="text-xs text-muted-foreground">Solution Bounties</span>
                    <span className="text-xs font-medium">${metrics.solutionBountiesAmount.toLocaleString()}</span>
                  </div>
                </HoverCardTrigger>
                <HoverCardContent side="right" sideOffset={16} className="w-80 bg-white dark:bg-gray-900 border shadow-lg z-50">
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold">Solution Bounties</h4>
                    <p className="text-xs text-muted-foreground">How much have you earned through capturing Solution Bounties?</p>
                  </div>
                </HoverCardContent>
              </HoverCard>

              <HoverCard openDelay={300} closeDelay={200}>
                <HoverCardTrigger asChild>
                  <div className="flex items-center justify-between p-2 hover:bg-accent rounded-md transition-colors cursor-pointer">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <span>Knowledge Bases</span>
                    </div>
                    <span className="font-semibold">{metrics.knowledgeBasesCount}</span>
                  </div>
                </HoverCardTrigger>
                <HoverCardContent side="right" sideOffset={16} className="w-[600px] p-0">
                  <KnowledgeBaseList userId={profile?.id} accessType="full" onNavigate={closeProfileDrawer} />
                </HoverCardContent>
              </HoverCard>

              {/* KB Sub-metrics with hover cards */}
              <HoverCard openDelay={300} closeDelay={200}>
                <HoverCardTrigger asChild>
                  <div className="flex items-center justify-between p-2 hover:bg-accent rounded-md transition-colors ml-4 cursor-pointer">
                    <span className="text-xs text-muted-foreground">Drafts</span>
                    <span className="text-xs font-medium">{metrics.knowledgeBasesCount - metrics.publishedKbsCount}</span>
                  </div>
                </HoverCardTrigger>
                <HoverCardContent side="right" sideOffset={16} className="w-80 max-h-96 overflow-y-auto bg-white dark:bg-gray-900 border shadow-lg z-50">
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold">Your Draft KBs</h4>
                    {detailedData.draftKbs.length > 0 ? (
                      <div className="space-y-2">
                        {detailedData.draftKbs.map(kb => (
                          <div key={kb.id} className="text-xs p-2 bg-accent/50 rounded">
                            <div className="font-medium truncate">{kb.title}</div>
                            <div className="text-muted-foreground">
                              Last modified: {new Date(kb.updated_at).toLocaleDateString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">No draft KBs</p>
                    )}
                  </div>
                </HoverCardContent>
              </HoverCard>

              <HoverCard openDelay={300} closeDelay={200}>
                <HoverCardTrigger asChild>
                  <div className="flex items-center justify-between p-2 hover:bg-accent rounded-md transition-colors ml-4 cursor-pointer">
                    <span className="text-xs text-muted-foreground">Minted</span>
                    <span className="text-xs font-medium">{metrics.publishedKbsCount}</span>
                  </div>
                </HoverCardTrigger>
                <HoverCardContent side="right" sideOffset={16} className="w-80 max-h-96 overflow-y-auto bg-white dark:bg-gray-900 border shadow-lg z-50">
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold">Your Minted KBs</h4>
                    {detailedData.mintedKbs.length > 0 ? (
                      <div className="space-y-2">
                        {detailedData.mintedKbs.map(kb => (
                          <div key={kb.id} className="text-xs p-2 bg-accent/50 rounded">
                            <div className="font-medium truncate">{kb.title}</div>
                            <div className="text-muted-foreground">
                              Minted: {kb.published_at ? new Date(kb.published_at).toLocaleDateString() : 'N/A'}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">No minted KBs</p>
                    )}
                  </div>
                </HoverCardContent>
              </HoverCard>

              <HoverCard openDelay={300} closeDelay={200}>
                <HoverCardTrigger asChild>
                  <div className="flex items-center justify-between p-2 hover:bg-accent rounded-md transition-colors ml-4 cursor-pointer">
                    <span className="text-xs text-muted-foreground">Licensed</span>
                    <span className="text-xs font-medium">{metrics.licensedKbsCount}</span>
                  </div>
                </HoverCardTrigger>
                <HoverCardContent side="right" sideOffset={16} className="w-80 max-h-96 overflow-y-auto bg-white dark:bg-gray-900 border shadow-lg z-50">
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold">Licensed KBs</h4>
                    <p className="text-xs text-muted-foreground mb-2">KBs you can access via channel membership, direct grants, or team attribution.</p>
                    {detailedData.licensedKbs.length > 0 ? (
                      <div className="space-y-2">
                        {detailedData.licensedKbs.map(kb => (
                          <div key={kb.id} className="text-xs p-2 bg-accent/50 rounded">
                            <div className="font-medium truncate">{kb.title}</div>
                            <div className="text-muted-foreground">
                              Via {kb.accessSourceName || kb.channel_name || 'Access Grant'}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">No licensed KBs</p>
                    )}
                  </div>
                </HoverCardContent>
              </HoverCard>

              <HoverCard>
                <HoverCardTrigger asChild>
                  <div className="flex items-center justify-between p-2 hover:bg-accent rounded-md transition-colors cursor-pointer">
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span>Calls</span>
                    </div>
                    <span className="font-semibold">{metrics.callsCount}</span>
                  </div>
                </HoverCardTrigger>
                <HoverCardContent side="right" sideOffset={16} className="w-80 max-h-96 overflow-y-auto bg-white dark:bg-gray-900 border shadow-lg z-50">
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold">Your Calls</h4>
                    {detailedData.calls.length > 0 ? <div className="space-y-2">
                        {detailedData.calls.map(call => <div key={call.id} className="text-xs p-2 bg-accent/50 rounded">
                            <div className="font-medium">{call.call_type}</div>
                            <div className="text-muted-foreground">Status: {call.status}</div>
                          </div>)}
                      </div> : <p className="text-xs text-muted-foreground">No calls yet</p>}
                  </div>
                </HoverCardContent>
              </HoverCard>

              <div className="flex items-center justify-between p-2 hover:bg-accent rounded-md transition-colors ml-4">
                <span className="text-xs text-muted-foreground">Sent</span>
                <span className="text-xs font-medium">{metrics.sentCallsCount}</span>
              </div>

              <div className="flex items-center justify-between p-2 hover:bg-accent rounded-md transition-colors ml-4">
                <span className="text-xs text-muted-foreground">Received</span>
                <span className="text-xs font-medium">{metrics.receivedCallsCount}</span>
              </div>

              {/* Sprint Metrics - Completed as primary */}
              <HoverCard openDelay={300} closeDelay={200}>
                <HoverCardTrigger asChild>
                  <div className="flex items-center justify-between p-2 hover:bg-accent rounded-md transition-colors cursor-pointer">
                    <div className="flex items-center gap-2">
                      <Zap className="h-4 w-4 text-muted-foreground" />
                      <span>Completed</span>
                    </div>
                    <span className="font-semibold">{metrics.completedSprintsTotal}</span>
                  </div>
                </HoverCardTrigger>
                <HoverCardContent side="right" sideOffset={16} className="w-80 max-h-96 overflow-y-auto bg-white dark:bg-gray-900 border shadow-lg z-50">
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold">All Completed Sprints</h4>
                    {(detailedData.createdSprints.length > 0 || detailedData.joinedSprints.length > 0) ? (
                      <div className="space-y-2">
                        {detailedData.createdSprints.map(sprint => (
                          <div key={sprint.id} className="text-xs p-2 bg-accent/50 rounded">
                            <div className="font-medium">{sprint.objective}</div>
                            <div className="text-muted-foreground">
                              Created • Completed: {new Date(sprint.completed_at).toLocaleDateString()}
                            </div>
                          </div>
                        ))}
                        {detailedData.joinedSprints.map(sprint => (
                          <div key={sprint.id} className="text-xs p-2 bg-accent/50 rounded">
                            <div className="font-medium">{sprint.objective}</div>
                            <div className="text-muted-foreground">
                              Joined • Completed: {sprint.completed_at ? new Date(sprint.completed_at).toLocaleDateString() : 'N/A'}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">No completed sprints yet</p>
                    )}
                  </div>
                </HoverCardContent>
              </HoverCard>

              {/* Created sub-metric */}
              <HoverCard openDelay={300} closeDelay={200}>
                <HoverCardTrigger asChild>
                  <div className="flex items-center justify-between p-2 hover:bg-accent rounded-md transition-colors ml-4 cursor-pointer">
                    <span className="text-xs text-muted-foreground">Created</span>
                    <span className="text-xs font-medium">{metrics.createdCompletedCount}</span>
                  </div>
                </HoverCardTrigger>
                <HoverCardContent side="right" sideOffset={16} className="w-80 max-h-96 overflow-y-auto bg-white dark:bg-gray-900 border shadow-lg z-50">
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold">Sprints You Created & Completed</h4>
                    {detailedData.createdSprints.length > 0 ? (
                      <div className="space-y-2">
                        {detailedData.createdSprints.map(sprint => (
                          <div key={sprint.id} className="text-xs p-2 bg-accent/50 rounded">
                            <div className="font-medium">{sprint.objective}</div>
                            <div className="text-muted-foreground">
                              Completed: {new Date(sprint.completed_at).toLocaleDateString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">No created sprints completed</p>
                    )}
                  </div>
                </HoverCardContent>
              </HoverCard>

              {/* Joined sub-metric */}
              <HoverCard openDelay={300} closeDelay={200}>
                <HoverCardTrigger asChild>
                  <div className="flex items-center justify-between p-2 hover:bg-accent rounded-md transition-colors ml-4 cursor-pointer">
                    <span className="text-xs text-muted-foreground">Joined</span>
                    <span className="text-xs font-medium">{metrics.joinedCompletedCount}</span>
                  </div>
                </HoverCardTrigger>
                <HoverCardContent side="right" sideOffset={16} className="w-80 max-h-96 overflow-y-auto bg-white dark:bg-gray-900 border shadow-lg z-50">
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold">Sprints You Joined & Completed</h4>
                    {detailedData.joinedSprints.length > 0 ? (
                      <div className="space-y-2">
                        {detailedData.joinedSprints.map(sprint => (
                          <div key={sprint.id} className="text-xs p-2 bg-accent/50 rounded">
                            <div className="font-medium">{sprint.objective}</div>
                            <div className="text-muted-foreground">
                              Completed: {sprint.completed_at ? new Date(sprint.completed_at).toLocaleDateString() : 'N/A'}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">No joined sprints completed</p>
                    )}
                  </div>
                </HoverCardContent>
              </HoverCard>

              {/* Pending Invitations sub-metric */}
              <HoverCard openDelay={300} closeDelay={200}>
                <HoverCardTrigger asChild>
                  <div className="flex items-center justify-between p-2 hover:bg-accent rounded-md transition-colors ml-4 cursor-pointer">
                    <span className="text-xs text-muted-foreground">Pending Invitations</span>
                    <span className="text-xs font-medium">{metrics.pendingSprintsCount}</span>
                  </div>
                </HoverCardTrigger>
                <HoverCardContent side="right" sideOffset={16} className="w-80 max-h-96 overflow-y-auto bg-white dark:bg-gray-900 border shadow-lg z-50">
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold">Pending Sprint Invitations</h4>
                    {detailedData.pendingInvitations.length > 0 ? (
                      <div className="space-y-2">
                        {detailedData.pendingInvitations.map(inv => (
                          <div key={inv.id} className="text-xs p-2 bg-accent/50 rounded">
                            <div className="font-medium">{inv.prompt_architecture_sprints?.objective || 'Sprint invitation'}</div>
                            <div className="text-muted-foreground">
                              Invited: {new Date(inv.invited_at).toLocaleDateString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">No pending invitations</p>
                    )}
                  </div>
                </HoverCardContent>
              </HoverCard>

              {/* In Progress sub-metric */}
              <HoverCard openDelay={300} closeDelay={200}>
                <HoverCardTrigger asChild>
                  <div className="flex items-center justify-between p-2 hover:bg-accent rounded-md transition-colors ml-4 cursor-pointer">
                    <span className="text-xs text-muted-foreground">In Progress</span>
                    <span className="text-xs font-medium">{metrics.inProgressSprintsCount}</span>
                  </div>
                </HoverCardTrigger>
                <HoverCardContent side="right" sideOffset={16} className="w-80 max-h-96 overflow-y-auto bg-white dark:bg-gray-900 border shadow-lg z-50">
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold">In Progress Sprints</h4>
                    {detailedData.inProgressSprints.length > 0 ? (
                      <div className="space-y-2">
                        {detailedData.inProgressSprints.map(sprint => (
                          <div key={sprint.id} className="text-xs p-2 bg-accent/50 rounded">
                            <div className="font-medium">{sprint.objective}</div>
                            <div className="text-muted-foreground">
                              Status: {sprint.status} • Started: {new Date(sprint.created_at).toLocaleDateString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">No sprints in progress</p>
                    )}
                  </div>
                </HoverCardContent>
              </HoverCard>
            </div>
          </div>

          <Separator />

          {/* Section 4: Help & Settings */}
          {isOwnProfile && <div>
              <h3 className="font-bold text-base mb-3 flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Help & Settings
              </h3>
              <div className="space-y-1">
                <button onClick={() => {
              closeProfileDrawer();
              navigate('/edit-profile');
            }} className="w-full flex items-center justify-between p-2 hover:bg-accent rounded-md transition-colors text-sm">
                  <span>Edit Profile</span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </button>
                <button onClick={() => {
              closeProfileDrawer();
              navigate('/pricing');
            }} className="w-full flex items-center justify-between p-2 hover:bg-accent rounded-md transition-colors text-sm">
                  <div className="flex items-center gap-2">
                    <Crown className="h-4 w-4 text-amber-500" />
                    <span>Subscription</span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </button>
                <button onClick={() => {
              closeProfileDrawer();
              navigate('/settings');
            }} className="w-full flex items-center justify-between p-2 hover:bg-accent rounded-md transition-colors text-sm">
                  <span>Settings</span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </button>
                <button onClick={async () => {
              await logout();
              closeProfileDrawer();
              navigate('/auth');
            }} className="w-full flex items-center justify-between p-2 hover:bg-accent rounded-md transition-colors text-sm text-destructive hover:text-destructive">
                  <span>Log Out</span>
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            </div>}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>;
};